import React, { useState, useEffect } from 'react';
import { X, MapPin } from 'lucide-react';
import { deliveryFeeService } from '../../services/deliveryFeeService';
import {
    DeliveryFeeConfig,
    DeliveryFeeConfigCreate,
    CalculationMethod,
    CALCULATION_METHOD_LABELS
} from '../../types/deliveryFee';
import { useToast } from '../../hooks/use-toast';
import DeliveryFeeTierEditor from './DeliveryFeeTierEditor.tsx';
import OpenStreetMapAddressPicker from '../ui/GoogleMapsAddressPicker';

interface Props {
    config: DeliveryFeeConfig | null;
    onClose: () => void;
}

const DeliveryFeeConfigForm: React.FC<Props> = ({ config, onClose }) => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [showTierEditor, setShowTierEditor] = useState(false);

    const [formData, setFormData] = useState<DeliveryFeeConfigCreate>({
        name: '',
        description: '',
        calculation_method: CalculationMethod.FLAT_RATE,
        is_active: true,
        is_default: false,
        base_fee: 0,
        free_delivery_threshold: undefined,
        price_per_km: undefined,
        min_distance_km: undefined,
        max_distance_km: undefined,
        price_per_kg: undefined,
        min_weight_kg: undefined,
        max_weight_kg: undefined,
        price_per_item: undefined,
        max_delivery_fee: undefined,
        applicable_regions: '',
        origin_address: '',
        origin_city: '',
        origin_postal_code: '',
        origin_country: 'France',
        origin_maps_link: '',
        priority: 0
    });

    useEffect(() => {
        if (config) {
            setFormData({
                name: config.name,
                description: config.description || '',
                calculation_method: config.calculation_method,
                is_active: config.is_active,
                is_default: config.is_default,
                base_fee: config.base_fee,
                free_delivery_threshold: config.free_delivery_threshold || undefined,
                price_per_km: config.price_per_km || undefined,
                min_distance_km: config.min_distance_km || undefined,
                max_distance_km: config.max_distance_km || undefined,
                price_per_kg: config.price_per_kg || undefined,
                min_weight_kg: config.min_weight_kg || undefined,
                max_weight_kg: config.max_weight_kg || undefined,
                price_per_item: config.price_per_item || undefined,
                max_delivery_fee: config.max_delivery_fee || undefined,
                applicable_regions: config.applicable_regions || '',
                origin_address: config.origin_address || '',
                origin_city: config.origin_city || '',
                origin_postal_code: config.origin_postal_code || '',
                origin_country: config.origin_country || 'France',
                origin_maps_link: config.origin_maps_link || '',
                priority: config.priority
            });
        }
    }, [config]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (config) {
                await deliveryFeeService.update(config.id, formData);
                toast({
                    title: 'Succès',
                    description: 'Configuration mise à jour'
                });
            } else {
                await deliveryFeeService.create(formData);
                toast({
                    title: 'Succès',
                    description: 'Configuration créée'
                });
            }
            onClose();
        } catch (error: any) {
            toast({
                title: 'Erreur',
                description: error.response?.data?.detail || 'Erreur lors de la sauvegarde',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const showFieldsForMethod = (method: CalculationMethod) => {
        return {
            perKm: [CalculationMethod.PER_KM, CalculationMethod.COMBINED].includes(method),
            perKg: [CalculationMethod.PER_KG, CalculationMethod.COMBINED].includes(method),
            perItem: [CalculationMethod.PER_ITEM, CalculationMethod.COMBINED].includes(method),
            tiered: [CalculationMethod.TIERED_DISTANCE, CalculationMethod.TIERED_WEIGHT].includes(method)
        };
    };

    const fields = showFieldsForMethod(formData.calculation_method);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                        {config ? 'Modifier la Configuration' : 'Nouvelle Configuration'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Nom *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Méthode de Calcul *
                            </label>
                            <select
                                value={formData.calculation_method}
                                onChange={(e) => setFormData({ ...formData, calculation_method: e.target.value as CalculationMethod })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {Object.entries(CALCULATION_METHOD_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-slate-300">Active</span>
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.is_default}
                                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-slate-300">Par défaut</span>
                            </label>
                        </div>
                    </div>

                    {/* Pricing Fields */}
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-6 space-y-4">
                        <h3 className="font-medium text-gray-900 dark:text-slate-100">Tarification</h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                    Frais de Base (€) *
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    required
                                    value={formData.base_fee}
                                    onChange={(e) => setFormData({ ...formData, base_fee: parseFloat(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                    Priorité
                                </label>
                                <input
                                    type="number"
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {fields.perKm && (
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Prix par km (€/km)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.price_per_km || ''}
                                        onChange={(e) => setFormData({ ...formData, price_per_km: parseFloat(e.target.value) || undefined })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Min Distance (km)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.min_distance_km || ''}
                                        onChange={(e) => setFormData({ ...formData, min_distance_km: parseFloat(e.target.value) || undefined })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Max Distance (km)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.max_distance_km || ''}
                                        onChange={(e) => setFormData({ ...formData, max_distance_km: parseFloat(e.target.value) || undefined })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        )}

                        {fields.perKg && (
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Prix par kg (€/kg)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.price_per_kg || ''}
                                        onChange={(e) => setFormData({ ...formData, price_per_kg: parseFloat(e.target.value) || undefined })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Min Poids (kg)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.min_weight_kg || ''}
                                        onChange={(e) => setFormData({ ...formData, min_weight_kg: parseFloat(e.target.value) || undefined })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Max Poids (kg)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.max_weight_kg || ''}
                                        onChange={(e) => setFormData({ ...formData, max_weight_kg: parseFloat(e.target.value) || undefined })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        )}

                        {fields.perItem && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                    Prix par Article (€/article)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.price_per_item || ''}
                                    onChange={(e) => setFormData({ ...formData, price_per_item: parseFloat(e.target.value) || undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                    Livraison Gratuite si ≥ (€)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.free_delivery_threshold || ''}
                                    onChange={(e) => setFormData({ ...formData, free_delivery_threshold: parseFloat(e.target.value) || undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                    Plafond Maximum (€)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.max_delivery_fee || ''}
                                    onChange={(e) => setFormData({ ...formData, max_delivery_fee: parseFloat(e.target.value) || undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {fields.tiered && config && (
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowTierEditor(true)}
                                    className="text-blue-600 hover:underline text-sm"
                                >
                                    Gérer les paliers de tarification
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Origin Address Section */}
                    {fields.perKm && (
                        <div className="border-t border-gray-200 dark:border-slate-700 pt-6 space-y-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-medium text-gray-900 dark:text-slate-100">📍 Adresse d'Origine</h3>
                                <span className="text-xs text-gray-500 dark:text-slate-400">Pour le calcul automatique de la distance</span>
                            </div>

                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-300 mb-4">
                                💡 Renseignez votre adresse (entrepôt/bureau) pour calculer automatiquement la distance vers vos clients
                            </div>

                            {/* GPS Address Picker */}
                            <div className="border-2 border-blue-300 dark:border-blue-700 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                                <label className="block text-sm font-medium mb-3 text-blue-700 dark:text-blue-300">
                                    <MapPin className="inline h-4 w-4 mr-1" />
                                    Localisation
                                </label>
                                <div className="min-h-[200px]">
                                    <OpenStreetMapAddressPicker
                                        onAddressSelect={(addressData: any) => {
                                            console.log('Address selected:', addressData);
                                            setFormData({
                                                ...formData,
                                                origin_address: addressData.address,
                                                origin_city: addressData.city,
                                                origin_postal_code: addressData.postal_code,
                                                origin_country: addressData.country,
                                                origin_maps_link: addressData.maps_link
                                            });
                                        }}
                                        initialAddress={formData.origin_address}
                                        initialCity={formData.origin_city}
                                        initialPostalCode={formData.origin_postal_code}
                                        initialCountry={formData.origin_country}
                                        initialMapsLink={formData.origin_maps_link}
                                    />
                                </div>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Cliquez sur la carte pour sélectionner une adresse</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Adresse
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.origin_address || ''}
                                        onChange={(e) => setFormData({ ...formData, origin_address: e.target.value })}
                                        placeholder="123 Rue Example"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Code Postal
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.origin_postal_code || ''}
                                        onChange={(e) => setFormData({ ...formData, origin_postal_code: e.target.value })}
                                        placeholder="75001"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Ville
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.origin_city || ''}
                                        onChange={(e) => setFormData({ ...formData, origin_city: e.target.value })}
                                        placeholder="Paris"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                        Pays
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.origin_country || 'France'}
                                        onChange={(e) => setFormData({ ...formData, origin_country: e.target.value })}
                                        placeholder="France"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 justify-end border-t border-gray-200 dark:border-slate-700 pt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? 'Enregistrement...' : config ? 'Mettre à Jour' : 'Créer'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Tier Editor Modal */}
            {showTierEditor && config && (
                <DeliveryFeeTierEditor
                    configId={config.id}
                    onClose={() => setShowTierEditor(false)}
                />
            )}
        </div>
    );
};

export default DeliveryFeeConfigForm;
